/* eslint-disable react-hooks/exhaustive-deps */
"use client"

import React, { useEffect, useRef, useMemo, useState, useCallback } from "react"
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend,
} from 'chart.js'
import { Bar } from 'react-chartjs-2'
import MaterialSelect from "../../components/MaterialSelect"
import { formatNumber } from "../../utils/data"

ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend,
);

const BudgetChart = ({
  title,
  selectedCoin,
  selectedStructure,
  setSelectedStructure,
  selectedRow,
  setSelectedRow,
  customStructure,
  itemsData = [],
  data,
  itemsPerPage = 10,
  scrollStep = 1,
  handleRowDoubleClick,
  options,
  mainColor,
}) => {
  const chartRef = useRef();
  const clickCountRef = useRef(0);
  const timeoutRef = useRef(null);
  const lastElementRef = useRef(null);
  const containerRef = useRef();
  const scrollThumbRef = useRef();
  const [isDragging, setIsDragging] = useState(false);
  const [startIndex, setStartIndex] = useState(0);

  const isMultiDatasetMode = useMemo(() => {
    return data && Array.isArray(data.datasets) && data.datasets.length > 1;
  }, [data]);

  const budgetData = useMemo(() => {
    if (isMultiDatasetMode) {
      const labels = data.labels || [];
      return labels.map((label, index) => ({
        id: index,
        label: label,
        amount: 0,
        fullLabel: label,
        datasetValues: data.datasets.map(dataset => dataset.data[index])
      }));
    } else {
      const rawData = itemsData.map(i => ({
        ...i,
        id: i.id,
        label: i.description,
        amount: (selectedCoin === 'PEN' ? i.SaldoPEN : i.SaldoUSD),
        fullLabel: i.description,
      }));
      
      return [...rawData].sort((a, b) => Math.abs(b.amount) - Math.abs(a.amount));
    }
  }, [itemsData, selectedCoin, data, isMultiDatasetMode]);

  const currentData = useMemo(() => {
    return budgetData.slice(startIndex, startIndex + itemsPerPage);
  }, [budgetData, startIndex, itemsPerPage]);

  const { minValue, maxValue } = useMemo(() => {
    if (isMultiDatasetMode) {
      const allValues = data.datasets.flatMap(dataset => dataset.data);
      const minAmount = Math.min(...allValues);
      const maxAmount = Math.max(...allValues);
      
      let calculatedMin = minAmount;
      let calculatedMax = maxAmount;
      
      if (minAmount >= 0) {
        calculatedMin = 0;
        calculatedMax = maxAmount;
      } 
      else if (maxAmount <= 0) {
        calculatedMin = minAmount;
        calculatedMax = 0;
      }
      else {
        calculatedMin = minAmount;
        calculatedMax = maxAmount;
      }
      const margin = Math.max(Math.abs(maxAmount), Math.abs(minAmount)) * 0.05;
      return {
        minValue: Number(calculatedMin < 0 ? calculatedMin - margin : calculatedMin).toFixed(0),
        maxValue: Number(calculatedMax > 0 ? calculatedMax + margin : calculatedMax).toFixed(0),
      };
    }

    if (currentData.length === 0) {
      return { minValue: 0, maxValue: 100 };
    }
    
    const amounts = currentData.map(item => item.amount);
    const minAmount = Math.min(...amounts);
    const maxAmount = Math.max(...amounts);
    
    let calculatedMin = minAmount;
    let calculatedMax = maxAmount;
    
    if (minAmount >= 0) {
      calculatedMin = 0;
      calculatedMax = maxAmount;
    } 
    else if (maxAmount <= 0) {
      calculatedMin = minAmount;
      calculatedMax = 0;
    }
    else {
      calculatedMin = minAmount;
      calculatedMax = maxAmount;
    }
    const margin = Math.max(Math.abs(maxAmount), Math.abs(minAmount)) * 0.05;
    return {
      minValue: Number(calculatedMin < 0 ? calculatedMin - margin : calculatedMin).toFixed(0),
      maxValue: Number(calculatedMax > 0 ? calculatedMax + margin : calculatedMax).toFixed(0),
    };
  }, [currentData, isMultiDatasetMode, data]);

  const totalSteps = useMemo(() => {
    if (budgetData.length <= itemsPerPage) return 1;
    const availableItems = budgetData.length - itemsPerPage;
    return Math.ceil(availableItems / scrollStep) + 1;
  }, [budgetData.length, itemsPerPage, scrollStep]);

  const currentStep = useMemo(() => {
    if (totalSteps <= 1) return 0;
    const step = Math.floor(startIndex / scrollStep);
    return Math.min(step, totalSteps - 1);
  }, [startIndex, scrollStep, totalSteps]);

  const thumbHeight = useMemo(() => {
    if (totalSteps <= 1) return 100;
    return 100 / totalSteps;
  }, [totalSteps]);

  const thumbPosition = useMemo(() => {
    if (totalSteps <= 1) return 0;
    const maxPosition = 100 - thumbHeight;
    const position = (currentStep / (totalSteps - 1)) * maxPosition;
    return Math.min(position, maxPosition);
  }, [currentStep, totalSteps, thumbHeight]);

  const handleMouseDown = useCallback((e) => {
    e.preventDefault();
    setIsDragging(true);
  }, []);

  const handleMouseMove = useCallback((e) => {
    if (!isDragging || !containerRef.current || totalSteps <= 1) return;

    const container = containerRef.current;
    const rect = container.getBoundingClientRect();
    const y = Math.max(0, Math.min(rect.height, e.clientY - rect.top));
    const percentage = (y / rect.height) * 100;
    const maxPosition = 100 - thumbHeight;
    const relativePosition = Math.max(0, Math.min(maxPosition, percentage));
    const stepPercentage = relativePosition / maxPosition;
    const newStep = Math.round(stepPercentage * (totalSteps - 1));
    
    const newStartIndex = Math.min(newStep * scrollStep, budgetData.length - itemsPerPage);
    setStartIndex(newStartIndex);
  }, [isDragging, totalSteps, scrollStep, budgetData.length, itemsPerPage, thumbHeight]);

  const handleMouseUp = useCallback(() => {
    setIsDragging(false);
  }, []);

  useEffect(() => {
    if (isDragging) {
      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseup', handleMouseUp);
      return () => {
        document.removeEventListener('mousemove', handleMouseMove);
        document.removeEventListener('mouseup', handleMouseUp);
      };
    }
  }, [isDragging, handleMouseMove, handleMouseUp]);


  const handleRowClick = (id) => {
    if (!setSelectedRow) {
      return;
    }
    if (selectedRow === id) {
      setSelectedRow('');
    } else {
      setSelectedRow(id);
    }
  };
  
  const handleChartClick = useCallback((event, elements) => {
    if (elements.length === 0) return;

    const element = elements[0];
    const index = element.index;
    const item = currentData[index];
    clickCountRef.current++;

    if (clickCountRef.current === 1) {
      timeoutRef.current = setTimeout(() => {
        if (clickCountRef.current === 1 && handleRowClick) {
          handleRowClick(item.id);
        }
        clickCountRef.current = 0;
        lastElementRef.current = null;
      }, 300);
    } else if (clickCountRef.current === 2) {
      clearTimeout(timeoutRef.current);
      if (lastElementRef.current === index && handleRowDoubleClick) {
        handleRowDoubleClick(item, element.datasetIndex);
      }
      
      clickCountRef.current = 0;
      lastElementRef.current = null;
    }

    lastElementRef.current = index;
  }, [currentData, handleRowClick, handleRowDoubleClick, isMultiDatasetMode]);

  const barThickness = useMemo(() => {
    const baseHeight = 20;
    const minHeight = 15;
    const maxHeight = 30;
    
    if (currentData.length <= 5) return maxHeight;
    if (currentData.length <= 10) return baseHeight;
    return minHeight;
  }, [currentData.length]);

  const chartData = useMemo(() => {
    if (isMultiDatasetMode) {
      const paginatedLabels = currentData.map(item => item.label);
      
      const paginatedDatasets = data.datasets.map(dataset => ({
        ...dataset,
        data: currentData.map((item, index) => dataset.data[startIndex + index])
      }));

      return {
        labels: paginatedLabels,
        datasets: paginatedDatasets
      };
    } else {
      return {
        labels: currentData.map(item => item.label),
        datasets: [
          {
            label: `Saldo`,
            data: currentData.map(item => item.amount),
            backgroundColor: currentData.map(item => 
              selectedRow === item.id ? "rgb(60, 141, 188)" : mainColor
            ),
            borderColor: currentData.map(item => 
              selectedRow === item.id ? "rgb(40, 121, 168)" : mainColor
            ),
            borderWidth: 1,
            barThickness: barThickness,
            borderRadius: 4,
          },
        ],
      };
    }
  }, [currentData, selectedRow, barThickness, isMultiDatasetMode, data, startIndex]);


  const chartOptions = {
    indexAxis: isMultiDatasetMode ? 'x' : 'y',
    responsive: true,
    maintainAspectRatio: false,
    layout: {
      padding: {
        right: 20,
      }
    },
    plugins: {
      legend: {
        display: false,
      },
      title: {
        display: true,
        text: `${title}`,
        font: {
          size: 12,
          weight: 'bold'
        },
        color: '#1976d2',
      },
      tooltip: {
        callbacks: {
          label: function(context) {
            const formattedValue = formatNumber(context.raw);
            return `${context.dataset.label}: ${formattedValue}`;
          },
          title: function(tooltipItems) {
            const item = currentData[tooltipItems[0].dataIndex];
            return item.fullLabel;
          },
          // También puedes formatear el valor cuando se muestran varios datasets
          afterBody: function(context) {
            if (isMultiDatasetMode && context.length > 1) {
              const total = context.reduce((sum, item) => sum + item.parsed, 0);
              return `Total: ${formatNumber(total)}`;
            }
            return null;
          }
        },
        displayColors: true,
        backgroundColor: 'rgba(255, 255, 255, 0.95)',
        titleColor: '#333',
        bodyColor: '#666',
        borderColor: '#ddd',
        borderWidth: 1
      }
    },
    scales: {
      x: {
        min: isMultiDatasetMode ? undefined : minValue,
        max: isMultiDatasetMode ? undefined : maxValue,
        grid: {
          display: true,
          color: 'rgba(0, 0, 0, 0.1)',
        },
        ticks: {
          font: {
            size: 10,
          },
          callback: function(value) {
            return formatNumber(value);
          }
        }
      },
      y: {
        grid: {
          display: false,
        },
        ticks: {
          font: {
            size: 10,
          },
          callback: function(value, index) {
            const label = currentData[index]?.label;
            if (label && label.length > 40) {
              return label.substring(0, 40) + '...';
            }
            return label;
          }
        }
      },
    },
    onClick: handleChartClick,
    onHover: (event, elements) => {
      const chart = chartRef.current;
      if (chart) {
        chart.canvas.style.cursor = elements.length > 0 ? 'pointer' : 'default';
      }
    },
  };


  return (
    <div style={{
      padding: '5px 16px',
      width: "100%",
      height: "100%",
      backgroundColor: "white",
      borderRadius: "4px",
      boxShadow: "0 1px 3px rgba(0,0,0,0.12), 0 1px 2px rgba(0,0,0,0.24)",
      display: 'flex',
      flexDirection: 'column'
    }}>
      <div style={{
        display: "flex",
        justifyContent: "space-between",
        alignItems: "center",
        flexWrap: 'wrap',
        gap: '10px'
      }}>
        {customStructure?.length && <div style={{ flex: 1 }}>
          <MaterialSelect
            label=""
            value={selectedStructure}
            onChange={(e) => setSelectedStructure(e.target.value)}
            width="8em"
          >
            {customStructure.map(e => (
              <option key={e.Nivel} value={e.Nivel}>{e.Descripcion}</option>))
            }
          </MaterialSelect>
        </div>}
      </div>

      <div style={{ 
        flex: 1, 
        display: 'flex',
        width: '100%',
        minHeight: 0,
        position: 'relative'
      }}>
        <div style={{ 
          flex: 1,
          position: 'relative',
          minWidth: 0, 
          maxHeight: `${((itemsPerPage <= currentData.length ? itemsPerPage : (currentData.length + 1)) + 2) * 3}em`,
        }}>
          <Bar 
            ref={chartRef}
            data={chartData} 
            options={options ? {...options, onClick: handleChartClick} : chartOptions}
            style={{
              width: '100%',
              height: '100%'
            }}
          />
        </div>

        {totalSteps > 1 && (
          <div
            ref={containerRef}
            style={{
              width: '16px',
              height: '100%',
              marginLeft: '8px',
              backgroundColor: '#f0f0f0',
              borderRadius: '8px',
              position: 'relative',
              cursor: 'pointer',
              flexShrink: 0,
              minHeight: '200px'
            }}
            onClick={(e) => {
              if (totalSteps <= 1) return;
              
              const container = e.currentTarget;
              const rect = container.getBoundingClientRect();
              const y = Math.max(0, Math.min(rect.height, e.clientY - rect.top));
              const percentage = (y / rect.height) * 100;
              const maxPosition = 100 - thumbHeight;
              const relativePosition = Math.max(0, Math.min(maxPosition, percentage));
              const stepPercentage = relativePosition / maxPosition;
              const newStep = Math.round(stepPercentage * (totalSteps - 1));
              
              const newStartIndex = Math.min(newStep * scrollStep, budgetData.length - itemsPerPage);
              setStartIndex(newStartIndex);
            }}
          >
            <div
              ref={scrollThumbRef}
              style={{
                position: 'absolute',
                top: `${thumbPosition}%`,
                width: '100%',
                height: `${thumbHeight}%`,
                backgroundColor: "rgb(60, 141, 188)",
                borderRadius: '8px',
                cursor: 'grab',
                transition: isDragging ? 'none' : 'top 0.2s ease',
                minHeight: '20px',
                border: '1px solid rgb(40, 121, 168)'
              }}
              onMouseDown={handleMouseDown}
            />
          </div>
        )}
      </div>
      <div style={{
        fontSize: '12px',
        color: '#666',
        textAlign: 'center'
      }}>
        Items {startIndex + 1}-{Math.min(startIndex + itemsPerPage, budgetData.length)} de {budgetData.length}
      </div>
    </div>
  )
}

export default BudgetChart;